import {
  FlexColumnContainer,
  FlexRowContainer,
  FlexRowWrapContainer,
  StrongText,
} from "@trailhead/core-ui";
import React, { ReactElement } from "react";
import styled from "styled-components";
import { trackEvent } from "@trailhead/analytics";
import { Button as AsurionButton, Text } from "@trailhead/asurion-ui";
import { useWindowDimensions } from "@trailhead/core-ui";
import { ExpertChatIcon } from "../assets";
import { QuestionsCTAProps } from "./propTypes";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { useChatClient } from "./useChatClient";
import { useIsChatEnabled } from "./useIsChatEnabled";
import { hasMissingEmbeddedContent } from "@trailhead/contentful";

const QuestionsContainer = styled.div`
  max-width: 600px;
  margin-top: 1rem;
  padding: 16px;
  display: flex;
  flex-direction: row;
  border-radius: 8px;
  background-color: #f0f0f5;
  align-self: left;
`;

const MarginContainer = styled.div`
  margin-bottom: 1rem;
`;

const CallToAction = styled(FlexColumnContainer)`
  align-items: left;
  width: 100%;
  gap: 12px;
`;

const CallToActionHeaderAndIcon = styled(FlexRowContainer)`
  gap: 12px;
`;

const CallToActionHeadersContainer = styled(FlexColumnContainer)`
  gap: 8px;
`;

const CallToActionButtonsContainer = styled(FlexRowWrapContainer)`
  gap: 8px;
`;

const CallToActionHeader = styled(Text)`
  text-align: left;
`;

const CallToActionSubHeader = styled(Text)`
  text-align: left;
  margin-top: 5px;
`;

const ImageWrapper = styled.div`
  text-align: left;
  margin-left: auto;
  max-width: 4rem;
  max-height: 4rem;
`;

const AsurionButtonWrapper = styled(AsurionButton)`
  align-self: flex-start;
  line-height: none;
  max-height: 32px;
  padding: 4px 12px;
`;

const AsurionFlatButtonWrapper = styled(AsurionButton)`
  height: auto;
  max-height: 3rem;
  text-decoration: none;
  cursor: default;
  padding-left: 1rem;

  &:hover {
    cursor: default !important;
    color: black !important;
  }
`;

export const QuestionsCTAWithCall: React.FC<QuestionsCTAProps> = ({
  content = {
    buttonText: "Chat",
    buttonVariant: "outline",
    buttonIcon: undefined,
    title: "Stuck on this step?",
    subtext: "Our experts can help.",
    secondaryButtonText: "Call us",
    secondaryButtonVariant: "default",
    supportNumber: undefined,
    icon: undefined,
  },
}) => {
  const t = useSanitizedTranslation();
  const ChatClient = useChatClient();
  const openChat = (): void => {
    trackEvent("QuestionsCTAWithCall_Chat_Click");
    ChatClient.openMessagingOverlay();
  };
  const enableMessaging = useIsChatEnabled();

  const { width } = useWindowDimensions();
  const isDesktop = width > 1080;

  const openCall = (): void => {
    if (content.supportNumber) {
      window.open(`tel: ${content.supportNumber}`, "_self");
      trackEvent("QuestionsCTAWithCall_Call_Click", {
        buttonText: content.secondaryButtonText,
      });
    }
  };

  const renderSubtext = (): string => {
    const subtext = content.subtext ? content.subtext : "";
    return hasMissingEmbeddedContent(subtext)
      ? "Our experts can help."
      : subtext;
  };

  const renderCallButton = (): ReactElement | null => {
    const iconSrc = "Phone";
    if (isDesktop && content.supportNumber && content.secondaryButtonText !== "<p>Call us</p>") {
      return (
        <AsurionFlatButtonWrapper iconSrc={iconSrc} size="medium" variant="text">
          <div
            dangerouslySetInnerHTML={{
              __html: t(content.secondaryButtonText as string),
            }}
          />
        </AsurionFlatButtonWrapper>
      );
    }

    if (content.supportNumber) {
      return (
        <AsurionButtonWrapper
          iconSrc={iconSrc}
          size="medium"
          variant={
            content.secondaryButtonVariant
              ? content.secondaryButtonVariant
              : "default"
          }
          onClick={openCall}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: t(content.secondaryButtonText as string),
            }}
          />
        </AsurionButtonWrapper>
      );
    }
    return null;
  };

  return (
    <MarginContainer>
      {enableMessaging && (
        <QuestionsContainer>
          <CallToAction>
            <CallToActionHeaderAndIcon>
              <CallToActionHeadersContainer>
                <StrongText>
                  <CallToActionHeader
                    size={3}
                    weight="heavy"
                    dangerouslySetInnerHTML={{
                      __html: t(content.title as string),
                    }}
                  />
                </StrongText>
                <CallToActionSubHeader
                  dangerouslySetInnerHTML={{
                    __html: t(renderSubtext()),
                  }}
                />
              </CallToActionHeadersContainer>
              <ImageWrapper>
                {content.icon ? content.icon : <ExpertChatIcon />}
              </ImageWrapper>
            </CallToActionHeaderAndIcon>
            <CallToActionButtonsContainer>
              <AsurionButtonWrapper
                iconSrc="ChatBubbleCircle"
                size="medium"
                variant={
                  content.supportNumber
                    ? "outline"
                    : content.buttonVariant
                }
                onClick={openChat}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: t(content.buttonText as string),
                  }}
                />
              </AsurionButtonWrapper>
              {renderCallButton()}
            </CallToActionButtonsContainer>
          </CallToAction>
        </QuestionsContainer>
      )}
    </MarginContainer>
  );
};
